import { z } from 'zod';
/* ============================================================================
   Shared primitives, pagination and the error envelope (docs/CONTRACTS.md)
   ========================================================================== */
export const Id = z.string().uuid();
/** ISO-8601 timestamp (UTC "Z" or with offset). Dates travel as strings on the wire. */
export const IsoDate = z.string().datetime({ offset: true });
/** Calendar date without time, e.g. due dates picked in the date picker. */
export const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/**
 * A public image (logo, icon) that works for everyone: https only. Rejects blob:/data:/http: URLs, which only
 * exist in one browser tab (a blob: logo was once saved and showed as a broken image in every header).
 */
export const HttpsImageUrl = z
    .string()
    .url()
    .max(2048)
    .refine((u) => /^https:\/\//i.test(u), 'Upload the image again (it must be an https:// link)');
/** Same rule for values read back from storage: anything unusable is served as null. */
export const httpsOrNull = (u) => (u && /^https:\/\//i.test(u) ? u : null);
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected #RRGGBB');
export const Email = z.string().trim().toLowerCase().email().max(254);
export const Url = z.string().url().max(2048);
export const Slug = z
    .string()
    .min(3)
    .max(30)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, digits and dashes');
/** Passwords: min 10 chars (PLAN §5.2). Strength is checked server-side too. */
export const Password = z.string().min(10).max(128);
export const IdParam = z.object({ id: Id });
export const OkSchema = z.object({ ok: z.literal(true) });
/** Empty body / no params marker. */
export const Empty = z.object({}).strict();
/* ---------- Cursor pagination (PLAN §9) ---------- */
export const PageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().min(1).optional(),
});
export const PageInfoSchema = z.object({
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    totalApprox: z.number().int().nonnegative().optional(),
});
/** Builds the paginated response schema for an item schema. */
export const pageOf = (item) => z.object({ data: z.array(item), page: PageInfoSchema });
/** Optional free-text search used by most list endpoints. */
export const SearchQuery = z.object({ q: z.string().trim().max(200).optional() });
/* ---------- Errors ---------- */
export const ErrorCodes = [
    'bad_request',
    'validation_failed',
    'unauthenticated',
    'invalid_credentials',
    'account_locked',
    'session_expired',
    'totp_required',
    'totp_invalid',
    'forbidden',
    'permission_denied',
    'entitlement_required',
    'limit_reached',
    'tenant_read_only',
    'tenant_suspended',
    'not_found',
    'conflict',
    'already_exists',
    'token_invalid',
    'token_expired',
    'token_used',
    'sole_owner',
    'domain_invalid',
    'domain_taken',
    'offer_unavailable',
    'rate_limited',
    'payload_too_large',
    'unsupported_media_type',
    'internal',
    'service_unavailable',
];
export const ErrorResponseSchema = z.object({
    error: z.object({
        code: z.enum(ErrorCodes),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
        requestId: z.string(),
    }),
});
/** Small user reference embedded in many payloads (avatars, assignees, actors). */
export const UserRefSchema = z.object({
    id: Id,
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().nullable(),
    initials: z.string().max(3),
    color: HexColor,
});
//# sourceMappingURL=common.js.map